import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { type OverlayToaster } from "@blueprintjs/core";
import ApiAdmin from "../api/admin";
import {
  serializeGroupPermissionsMap,
  serializeGroupPermissionsMapWithout,
  serializeUserPermissionsMap,
  serializeUserPermissionsMapWithout,
} from "../utils/permissions";

const MAX_FOLDER_NAME_LENGTH = 64;
const FOLDER_NAME_REGEX = /^[a-zA-Z0-9 _.-]+$/;

interface Folder {
  id: number;
  label: string;
  user_ids: number[];
  group_ids: number[];
  organization_order_type_ids: number[];
  deleted_at?: string | null;
}

interface CreateFolderParams {
  name: string;
  user_ids: number[];
  group_ids: number[];
}

interface UpdateFolderParams {
  folderId: number;
  name: string;
}

interface UpdatePermissionsParams {
  folderId: number;
  userPermissions: Map<number, number>;
  groupPermissions: Map<number, number>;
}

interface RemoveUserPermissionParams {
  folderId: number;
  userId: number;
  userPermissions: Map<number, number>;
}

interface RemoveGroupPermissionParams {
  folderId: number;
  groupId: number;
  groupPermissions: Map<number, number>;
}

interface FoldersQueryData {
  folders: Folder[];
}

/**
 * Validates a folder name against business rules.
 * @param name - The folder name to validate
 * @param existingFolders - Array of existing folders to check for duplicates
 * @param currentId - Optional folder ID to exclude from duplicate check (for updates)
 * @throws Error with descriptive message if validation fails
 */
function validateFolderName(
  name: string,
  existingFolders: Folder[],
  currentId?: number
): void {
  const trimmedName = name.trim();

  if (!trimmedName) {
    throw new Error("Folder name cannot be empty.");
  }

  if (trimmedName.length > MAX_FOLDER_NAME_LENGTH) {
    throw new Error(
      `Folder name cannot exceed ${MAX_FOLDER_NAME_LENGTH} characters.`
    );
  }

  if (!FOLDER_NAME_REGEX.test(trimmedName)) {
    throw new Error(
      "Folder name can only contain letters, numbers, spaces, underscores, dashes, and periods."
    );
  }

  const duplicateFolder = existingFolders.find(
    (folder) =>
      folder.id !== currentId &&
      folder.label.trim().toLowerCase() === trimmedName.toLowerCase()
  );

  if (duplicateFolder) {
    throw new Error(`A folder with the name "${trimmedName}" already exists.`);
  }
}

export function useFolderMutations(organizationId: number) {
  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: ({ name, user_ids, group_ids }: CreateFolderParams) => {
      return ApiAdmin.createFolder(organizationId, {
        label: name.trim(),
        user_ids,
        group_ids,
      });
    },
    onMutate: async ({ name, user_ids, group_ids }) => {
      await queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      await queryClient.cancelQueries({
        queryKey: ["users", organizationId],
      });
      await queryClient.cancelQueries({
        queryKey: ["groups", organizationId],
      });

      const foldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolders = foldersQueries[0]?.[1];
      const existingFolders = previousFolders?.folders || [];

      validateFolderName(name, existingFolders);

      const previousUsers = queryClient.getQueryData(["users", organizationId]);
      const previousGroups = queryClient.getQueryData([
        "groups",
        organizationId,
      ]);

      const tempId = Date.now();
      queryClient.setQueriesData(
        { queryKey: ["folders", organizationId], exact: false },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            folders: [
              ...old.folders,
              {
                id: tempId,
                label: name,
                user_ids,
                group_ids,
              },
            ],
          };
        }
      );

      return { previousFolders, previousUsers, previousGroups };
    },
    onError: (err, variables, context) => {
      if (context?.previousFolders) {
        queryClient.setQueriesData(
          { queryKey: ["folders", organizationId], exact: false },
          context.previousFolders
        );
      }
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId],
          context.previousUsers
        );
      }
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", organizationId],
          context.previousGroups
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to create folder. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Folder created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", organizationId],
      });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: UpdateFolderParams) => {
      return ApiAdmin.updateFolder(organizationId, folderId, {
        label: name.trim(),
      });
    },
    onMutate: async ({ folderId, name }) => {
      await queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });

      const foldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolders = foldersQueries[0]?.[1];
      const existingFolders = previousFolders?.folders || [];

      validateFolderName(name, existingFolders, folderId);

      queryClient.setQueriesData(
        { queryKey: ["folders", organizationId], exact: false },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            folders: old.folders.map((folder: Folder) =>
              folder.id === folderId ? { ...folder, label: name } : folder
            ),
          };
        }
      );

      return { previousFolders };
    },
    onError: (err, { folderId }, context) => {
      if (context?.previousFolders) {
        queryClient.setQueriesData(
          { queryKey: ["folders", organizationId], exact: false },
          context.previousFolders
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to update folder. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Folder updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", organizationId],
      });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: number) =>
      ApiAdmin.deleteFolder(organizationId, folderId),
    onMutate: async (folderId: number) => {
      await queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });

      const foldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolders = foldersQueries[0]?.[1];

      const deletedAtTimestamp = new Date().toISOString();
      foldersQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) return;

        const includeDeactivated = queryKey[2] === true;

        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old) return old;

          if (includeDeactivated) {
            return {
              ...old,
              folders: old.folders.map((folder: Folder) =>
                folder.id === folderId
                  ? { ...folder, deleted_at: deletedAtTimestamp }
                  : folder
              ),
            };
          } else {
            return {
              ...old,
              folders: old.folders.filter(
                (folder: Folder) => folder.id !== folderId
              ),
            };
          }
        });
      });

      return { previousFolders };
    },
    onError: (err, folderId, context) => {
      if (context?.previousFolders) {
        queryClient.setQueriesData(
          { queryKey: ["folders", organizationId], exact: false },
          context.previousFolders
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to deactivate folder. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Folder deactivated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", organizationId],
      });
    },
  });

  const activateFolderMutation = useMutation({
    mutationFn: (folderId: number) =>
      ApiAdmin.activateFolder(organizationId, folderId),
    onMutate: async (folderId: number) => {
      await queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });

      const foldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolders = foldersQueries[0]?.[1];

      queryClient.setQueriesData(
        { queryKey: ["folders", organizationId], exact: false },
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            folders: old.folders.map((folder: Folder) =>
              folder.id === folderId ? { ...folder, deleted_at: null } : folder
            ),
          };
        }
      );

      return { previousFolders };
    },
    onError: (err, folderId, context) => {
      if (context?.previousFolders) {
        queryClient.setQueriesData(
          { queryKey: ["folders", organizationId], exact: false },
          context.previousFolders
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to activate folder. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Folder activated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["groups", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization", organizationId],
      });
    },
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: ({
      folderId,
      userPermissions,
      groupPermissions,
    }: UpdatePermissionsParams) => {
      const userPerms = serializeUserPermissionsMap(userPermissions);
      const groupPerms = serializeGroupPermissionsMap(groupPermissions);

      return ApiAdmin.updateFolderPermissions(organizationId, folderId, {
        user_permissions: userPerms,
        group_permissions: groupPerms,
      });
    },
    onMutate: async ({ folderId }) => {
      await queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      await queryClient.cancelQueries({ queryKey: ["users", organizationId] });
      await queryClient.cancelQueries({ queryKey: ["groups", organizationId] });

      const foldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolders = foldersQueries[0]?.[1];
      const previousUsers = queryClient.getQueryData(["users", organizationId]);
      const previousGroups = queryClient.getQueryData([
        "groups",
        organizationId,
      ]);

      return { previousFolders, previousUsers, previousGroups };
    },
    onError: (err, variables, context) => {
      if (context?.previousFolders) {
        queryClient.setQueriesData(
          { queryKey: ["folders", organizationId], exact: false },
          context.previousFolders
        );
      }
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId],
          context.previousUsers
        );
      }
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", organizationId],
          context.previousGroups
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message || "Failed to update permissions. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["folderPermissions", organizationId, variables.folderId],
      });

      if (toaster.current) {
        toaster.current.show({
          message: "Permissions updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
  });

  const removeUserPermissionMutation = useMutation({
    mutationFn: ({
      folderId,
      userId,
      userPermissions,
    }: RemoveUserPermissionParams) => {
      return ApiAdmin.updateFolderPermissions(organizationId, folderId, {
        user_permissions: serializeUserPermissionsMapWithout(userPermissions, userId),
      });
    },
    onError: (err) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message ||
            "Failed to remove user permission. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["folderPermissions", organizationId, variables.folderId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "computedFolderPermissions",
          organizationId,
          variables.folderId,
        ],
      });

      if (toaster.current) {
        toaster.current.show({
          message: "User removed from folder successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
  });

  const removeGroupPermissionMutation = useMutation({
    mutationFn: ({
      folderId,
      groupId,
      groupPermissions,
    }: RemoveGroupPermissionParams) => {
      return ApiAdmin.updateFolderPermissions(organizationId, folderId, {
        group_permissions: serializeGroupPermissionsMapWithout(groupPermissions, groupId),
      });
    },
    onError: (err) => {
      if (toaster.current) {
        toaster.current.show({
          message:
            err.message ||
            "Failed to remove group permission. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["folderPermissions", organizationId, variables.folderId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "computedFolderPermissions",
          organizationId,
          variables.folderId,
        ],
      });

      if (toaster.current) {
        toaster.current.show({
          message: "Group removed from folder successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
    },
  });

  return {
    createFolder: createFolderMutation.mutate,
    createFolderAsync: createFolderMutation.mutateAsync,
    updateFolder: updateFolderMutation.mutate,
    updateFolderAsync: updateFolderMutation.mutateAsync,
    deleteFolder: deleteFolderMutation.mutate,
    activateFolder: activateFolderMutation.mutate,
    updatePermissions: updatePermissionsMutation.mutate,
    updatePermissionsAsync: updatePermissionsMutation.mutateAsync,
    removeUserPermission: removeUserPermissionMutation.mutate,
    removeUserPermissionAsync: removeUserPermissionMutation.mutateAsync,
    removeGroupPermission: removeGroupPermissionMutation.mutate,
    removeGroupPermissionAsync: removeGroupPermissionMutation.mutateAsync,
    isCreating: createFolderMutation.isPending,
    isUpdating: updateFolderMutation.isPending,
    isDeleting: deleteFolderMutation.isPending,
    isActivating: activateFolderMutation.isPending,
    isUpdatingPermissions: updatePermissionsMutation.isPending,
    isRemovingUserPermission: removeUserPermissionMutation.isPending,
    isRemovingGroupPermission: removeGroupPermissionMutation.isPending,
  };
}

type UpdateFolderOrderTypesParams = {
  folderId: number;
  organizationOrderTypeIds: number[];
};

export function useUpdateFolderOrderTypesMutation(
  organizationId: number,
  opts?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, organizationOrderTypeIds }: UpdateFolderOrderTypesParams) =>
      ApiAdmin.updateFolderOrderTypes(organizationId, folderId, {
        organization_order_type_ids: organizationOrderTypeIds,
      }),
    onMutate: async ({ folderId, organizationOrderTypeIds }) => {
      // Fire-and-forget: we want the optimistic write to land immediately
      // so subsequent rapid clicks read the optimistic state.
      void queryClient.cancelQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      void queryClient.cancelQueries({
        queryKey: ["folderOrderTypes", folderId],
      });

      const previousFoldersQueries = queryClient.getQueriesData<FoldersQueryData>({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      const previousFolderOrderTypes = queryClient.getQueryData<{
        organization_order_type_ids: number[];
      }>(["folderOrderTypes", folderId]);

      // Keep the list view (folders query) in sync so rapid clicks read optimistic state.
      previousFoldersQueries.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old: FoldersQueryData | undefined) => {
          if (!old) return old;
          return {
            ...old,
            folders: old.folders.map((folder) =>
              folder.id === folderId
                ? {
                    ...folder,
                    organization_order_type_ids: organizationOrderTypeIds,
                  }
                : folder
            ),
          };
        });
      });

      // Also update the per-folder query if it's in use elsewhere.
      queryClient.setQueryData(["folderOrderTypes", folderId], {
        organization_order_type_ids: organizationOrderTypeIds,
      });

      return { previousFoldersQueries, previousFolderOrderTypes, folderId };
    },
    onSuccess: (_data, variables) => {
      opts?.onSuccess?.();
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFoldersQueries) {
        context.previousFoldersQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
      if (context?.folderId) {
        if (context.previousFolderOrderTypes === undefined) {
          queryClient.removeQueries({
            queryKey: ["folderOrderTypes", context.folderId],
            exact: true,
          });
        } else {
          queryClient.setQueryData(
            ["folderOrderTypes", context.folderId],
            context.previousFolderOrderTypes
          );
        }
      }
      opts?.onError?.(error);
    },
    onSettled: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folders", organizationId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["folderOrderTypes", variables.folderId],
      });
    },
  });
}
